import React, { useState, useEffect } from 'react';
import { getFaqs } from '../services/api';
import { Faq } from '../types';
import { useTranslation } from '../context/TranslationContext';
import './FaqPage.css';

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setLoading(true);
        const data = await getFaqs();
        setFaqs(data);
        
        // Initialize expanded state for each FAQ
        const initialExpandedState: Record<string, boolean> = {};
        data.forEach((faq) => {
          const id = faq._id || faq.id?.toString() || '';
          initialExpandedState[id] = false;
        });
        setExpandedItems(initialExpandedState);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        setError(t('faq.noFaqs'));
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, [t]);

  const toggleItem = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const expandAll = () => {
    const allExpanded: Record<string, boolean> = {};
    faqs.forEach((faq) => {
      const id = faq._id || faq.id?.toString() || '';
      allExpanded[id] = true;
    });
    setExpandedItems(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed: Record<string, boolean> = {};
    faqs.forEach((faq) => {
      const id = faq._id || faq.id?.toString() || '';
      allCollapsed[id] = false;
    });
    setExpandedItems(allCollapsed);
  };

  return (
    <div className="faq-page">
      <div className="faq-header">
        <h1>{t('faq.title')}</h1>
        <p>{t('faq.subtitle')}</p>
      </div>
      
      {loading ? (
        <div className="loading">Betöltés...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          {faqs.length > 0 ? (
            <>
              <div className="faq-controls">
                <button className="btn btn-secondary" onClick={expandAll}>
                  {t('faq.expandAll')}
                </button>
                <button className="btn btn-secondary" onClick={collapseAll}>
                  {t('faq.collapseAll')}
                </button>
              </div>
              
              <div className="faq-list">
                {faqs.map((faq) => {
                  const id = faq._id || faq.id?.toString() || '';
                  const isExpanded = expandedItems[id] || false;
                  
                  return (
                    <div key={id} className={`faq-item ${isExpanded ? 'expanded' : ''}`}>
                      <div 
                        className="faq-question" 
                        onClick={() => toggleItem(id)}
                      >
                        <h3>{faq.question}</h3>
                        <span className="faq-toggle">
                          {isExpanded ? '−' : '+'}
                        </span>
                      </div>
                      
                      {isExpanded && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-faqs">{t('faq.noFaqs')}</div>
          )}
        </>
      )}
    </div>
  );
};

export default FaqPage;
